/**
 * Line entity → `RenderableEntity` adapter — TYPED variant (DEFUSE S3).
 *
 * Counterpart of the legacy `lineToRenderable.ts` adapter that reads the
 * `EntityPreview.properties` string bag ("Start X / Y / Z", "End X / Y / Z").
 * This typed adapter reads the canonical `LineEntity` fields off the
 * `DrawingEntity` model directly (`start` / `end`, each a
 * `drawingPointOptionalZSchema` point), applies the SAME locked-in coordinate
 * origin offset (`applyOffset`), and builds the byte-identical
 * `SceneGeometry` + bounds so the S3 golden-parity harness can compare the two
 * paths.
 *
 * z handling: `start.z` / `end.z` are *optional* on the model
 * (`drawingPointOptionalZSchema`, DEFUSE S1b). The legacy adapter read the
 * "Start Z" / "End Z" bag keys via `readNumber`, which falls back to `0` when
 * the key is absent — so this adapter reads `start.z ?? 0` / `end.z ?? 0` to
 * produce the identical `Vec3` (z is required + numeric on `Vec3`) before the
 * offset, keeping parity.
 *
 * Color resolution (BYLAYER → BYBLOCK → explicit) and lineweight live in the
 * shared `buildRenderableShell` (typed overload reads `entity.layer` /
 * `entity.color` / `entity.lineweight` / `entity.linetype` /
 * `entity.linetypeScale`); this adapter is geometry-only.
 *
 * Category B — NOT wired into any render path yet. The legacy
 * `lineToRenderable` remains the sole adapter the pipeline calls.
 */

import type { LineEntity } from "@dxf-ai-editor/drawing-model";

import type { RenderableEntity, Vec3 } from "../../types";

import {
  buildRenderableShell,
  type AdapterContext,
} from "../adapterContext";
import { buildLineSceneGeometry } from "../sceneGeometryBuilders";

/** Typed adapter for the `Line` (DXF LINE) entity. */
export function lineFromEntity(
  entity: LineEntity,
  ctx: AdapterContext,
): RenderableEntity {
  const aRaw: Vec3 = {
    x: entity.start.x,
    y: entity.start.y,
    z: entity.start.z ?? 0,
  };
  const bRaw: Vec3 = {
    x: entity.end.x,
    y: entity.end.y,
    z: entity.end.z ?? 0,
  };

  const { geometry, bounds } = buildLineSceneGeometry(aRaw, bRaw);

  return buildRenderableShell(entity, ctx, geometry, bounds);
}
